import React, { useMemo, useState, useEffect } from 'react';
import { useDiary, pixelY, formatTime } from './DiaryContext';
import moment from 'moment';

const HEADER_HEIGHT = typeof window !== 'undefined' && window.innerWidth < 768 ? 108 : 96;

/**
 * TimeAxis - Sticky-left time labels column.
 * Renders hour labels (and sub-labels at high zoom) positioned
 * absolutely within the diary viewport.
 * Also renders the "now" time pill (Apple Calendar style).
 */
function TimeAxis() {
  const { state } = useDiary();
  const { viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes, viewMode, selectedDate } = state;

  const labels = useMemo(() => {
    const items = [];
    // Determine label interval based on zoom and slot duration
    let interval = 60; // default: every hour
    if (zoomLevel >= 2.0 && slotDurationMinutes <= 15) interval = 30;
    if (zoomLevel >= 2.5 && slotDurationMinutes <= 10) interval = 15;
    if (zoomLevel <= 0.75) interval = 120; // every 2 hours at low zoom

    // Start from the first interval boundary at or after viewStart
    const start = Math.ceil(viewStartMinutes / interval) * interval;

    for (let m = start; m <= viewEndMinutes; m += interval) {
      items.push({
        minutes: m,
        label: formatTime(m),
        isMajor: m % 60 === 0,
      });
    }
    return items;
  }, [viewStartMinutes, viewEndMinutes, zoomLevel, slotDurationMinutes]);

  // Now time pill (Apple Calendar style) - only on today
  const isToday = selectedDate === moment().format('YYYY-MM-DD');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!isToday) return;
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, [isToday]);

  // Always read fresh time on each render
  const pillNow = new Date();
  const nowMinutes = pillNow.getHours() * 60 + pillNow.getMinutes();
  const nowLabel = `${String(pillNow.getHours()).padStart(2, '0')}:${String(pillNow.getMinutes()).padStart(2, '0')}`;
  const nowTop = HEADER_HEIGHT + pixelY(nowMinutes, viewStartMinutes, zoomLevel);

  return (
    <div className={`dv2-time-axis ${viewMode === 'week' ? 'dv2-time-axis--week' : ''}`}>
      {labels.map(item => (
        <div
          key={item.minutes}
          className={`dv2-time-label ${item.isMajor ? 'dv2-time-label--major' : 'dv2-time-label--minor'}`}
          style={{ top: HEADER_HEIGHT + pixelY(item.minutes, viewStartMinutes, zoomLevel) }}
        >
          <span>{item.label}</span>
        </div>
      ))}
      {isToday && (
        <div className="dv2-time-axis-pill" style={{ top: nowTop }}>
          {nowLabel}
        </div>
      )}
    </div>
  );
}

export default React.memo(TimeAxis);
